import { localToEpochMin, zonedParts } from "./slots";
import { nameSkeleton, normalizeText } from "./validate";
import type { OptionTally } from "./score";
import type { EventState, Level, LogEntry, PollDef } from "./types";

export const STATUS_COPY = {
  idle: "All changes saved",
  saved: "All changes saved",
  saving: "Saving",
  dirty: "Unsaved changes",
  retrying: "Offline. Retrying.",
  error: "Could not save",
  locked: "Locked by the organizer",
  gone: "You are no longer in this poll",
};

export const LEVEL_LABEL: Record<Level, string> = {
  0: "empty",
  1: "if need be",
  2: "works",
  3: "preferred",
};

export function heatStep(available: number, total: number): 0 | 1 | 2 | 3 | 4 | 5 {
  if (total <= 0 || available <= 0) return 0;
  if (available === total && total > 0) return 5;
  const stepped = Math.round((available / total) * 4);
  if (stepped < 1) return 1;
  if (stepped > 4) return 4;
  return stepped as 1 | 2 | 3 | 4;
}

export function tiedLeaders(tallies: OptionTally[]): string[] {
  if (tallies.length < 2) return [];
  const best = tallies[0];
  if (!best) return [];
  if (best.acceptable < 1) return [];
  const ids = tallies
    .filter((t) => t.acceptable === best.acceptable && t.score === best.score && t.no === best.no)
    .map((t) => t.optionId);
  if (ids.length < 2) return [];
  return ids;
}

export function waitingOn(state: EventState): string[] {
  // nameSkeleton, not toLowerCase: it is the rule the rest of the codebase uses to decide whether
  // two spellings are one person (room.ts's name-taken check, validate.ts's expected list). A bare
  // lowercase disagrees with it on any name whose accent can be typed two ways, and a phone
  // keyboard commonly produces the decomposed one, so an organizer who typed "José" was told she
  // had not answered when she had.
  const have = new Set(state.participants.map((p) => nameSkeleton(p.name)));
  return state.expected.filter((n) => !have.has(nameSkeleton(n)));
}

/** The people who answered under a name the organizer never wrote down.
 *
 * jev-interview: pass, matching a nickname to an expected name is the organizer's call and the
 * names of real attendees must not leave the Worker to be classified.
 *
 * On its own this list is trivia. Beside `waitingOn` it asks the question the organizer actually
 * needs asked. `nameSkeleton` settles the spellings that are mechanically one person, which is why
 * an organizer who typed "José" is no longer told she never answered. It cannot settle "Bobby"
 * against "Robert", and nothing here should try: a classifier that guessed would be deciding who is
 * on her list, which is hers to decide, and it could only do it by sending her attendees' real
 * names somewhere else. So the tool puts the two facts side by side and she settles it by editing
 * the list she already owns.
 *
 * Empty when there is no expected list, because then every answer is unmatched and the line is
 * noise. The caller shows it only while someone is still outstanding: once everyone expected has
 * answered, an extra guest is just a guest. */
export function unmatchedAnswers(state: EventState): string[] {
  if (state.expected.length === 0) return [];
  const want = new Set(state.expected.map((n) => nameSkeleton(n)));
  return state.participants.filter((p) => !want.has(nameSkeleton(p.name))).map((p) => p.name);
}

export interface EventFields {
  title: string;
  note: string;
  timezone: string;
  dates: string[];
  dayStartMin: number;
  dayEndMin: number;
  durationMin: number;
  polls: PollDef[];
  deadline: string | null;
  expected: string[];
}

function sameList(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

const FIELD_ORDER: (keyof EventFields)[] = [
  "title",
  "note",
  "timezone",
  "dates",
  "dayStartMin",
  "dayEndMin",
  "durationMin",
  "polls",
  "deadline",
  "expected",
];

const FIELD_LABEL: Record<keyof EventFields, string> = {
  title: "title",
  note: "note",
  timezone: "timezone",
  dates: "dates",
  dayStartMin: "start time",
  dayEndMin: "end time",
  durationMin: "meeting length",
  polls: "polls",
  deadline: "deadline",
  expected: "expected names",
};

/** Field-by-field diff of a form against its baseline: `title` and `note` compare trimmed, `dates`
 * and `expected` as ordered lists, `polls` as JSON, everything else with `===`. Only the fields
 * that actually differ come back, so a field the organizer never touched is never sent to the
 * server and cannot clobber whatever someone else did to it. */
export function changedEventFields(baseline: EventFields, form: EventFields): Partial<EventFields> {
  const out: Partial<EventFields> = {};
  if (baseline.title.trim() !== form.title.trim()) out.title = form.title;
  if (baseline.note.trim() !== form.note.trim()) out.note = form.note;
  if (baseline.timezone !== form.timezone) out.timezone = form.timezone;
  if (!sameList(baseline.dates, form.dates)) out.dates = form.dates;
  if (baseline.dayStartMin !== form.dayStartMin) out.dayStartMin = form.dayStartMin;
  if (baseline.dayEndMin !== form.dayEndMin) out.dayEndMin = form.dayEndMin;
  if (baseline.durationMin !== form.durationMin) out.durationMin = form.durationMin;
  if (JSON.stringify(baseline.polls) !== JSON.stringify(form.polls)) out.polls = form.polls;
  if (baseline.deadline !== form.deadline) out.deadline = form.deadline;
  if (!sameList(baseline.expected, form.expected)) out.expected = form.expected;
  return out;
}

/** A field is in conflict when the server's live value and this form's value have each drifted
 * from the same baseline: an organizer's stale edit to a field nobody else touched is never a
 * conflict, and a remote edit to a field this organizer never touched is never a conflict either. */
export function conflictingFields(
  baseline: EventFields,
  server: EventFields,
  form: EventFields,
): (keyof EventFields)[] {
  const changedByServer = changedEventFields(baseline, server);
  const changedByForm = changedEventFields(baseline, form);
  const out: (keyof EventFields)[] = [];
  for (const key of FIELD_ORDER) {
    if (key in changedByServer && key in changedByForm) out.push(key);
  }
  return out;
}

function joinWithAnd(items: readonly string[]): string {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0] ?? "";
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}

export function conflictNotice(fields: readonly (keyof EventFields)[]): string {
  if (fields.length === 0) return "";
  const named = joinWithAnd(fields.map((f) => `the ${FIELD_LABEL[f]}`));
  return `Someone else also changed ${named}. Saving will keep yours.`;
}

/** Whether the note box should be given the draft's note. Typing updates the draft at once, so a
 * difference means the draft changed from somewhere else (my other tab, a merge) and the box must
 * follow it even while it has focus: otherwise the next keystroke writes the old text back over
 * the newer note. The one exception is my own save coming back normalized (a trailing space
 * trimmed while I am still typing), which must not move the text under the caret. */
export function shouldAssignNote(domValue: string, draftNote: string, focused: boolean): boolean {
  if (domValue === draftNote) return false;
  if (focused && normalizeText(domValue) === draftNote) return false;
  return true;
}

/** Turns the poll builder's raw rows into what a save may carry, or says why it cannot. A poll
 * that is completely empty is ignored (someone pressed "Add poll" and left it). Blank option rows
 * inside a poll are dropped. After that, an unfinished poll is an ERROR, never a deletion: dropping
 * it silently turned "add a blank option, remove an old one, press Save" into deleting the poll. */
export function pollsForSave(raw: readonly PollDef[]): { ok: true; polls: PollDef[] } | { ok: false; error: string } {
  const polls: PollDef[] = [];
  for (const poll of raw) {
    const question = poll.question.trim();
    const options = poll.options
      .map((o) => ({ id: o.id, label: o.label.trim() }))
      .filter((o) => o.label.length > 0);
    if (question.length === 0 && options.length === 0) continue;
    if (question.length === 0) return { ok: false, error: "Every poll needs a question." };
    if (options.length < 2) return { ok: false, error: `The poll "${question}" needs at least two options.` };
    polls.push({ id: poll.id, question, options });
  }
  return { ok: true, polls };
}

/** True when two different slots read as the same date and clock time in `tz`: the viewer's own
 * fall-back night, where a civil time occurs twice. `buildGridModel` keys cells by date and clock
 * time in the viewing zone, so a caller must check this before choosing which zone to view in. */
export function viewerZoneCollides(slots: readonly number[], tz: string): boolean {
  const seen = new Set<string>();
  for (const s of slots) {
    const p = zonedParts(s, tz);
    const key = `${p.date}|${p.minutes}`;
    if (seen.has(key)) return true;
    seen.add(key);
  }
  return false;
}

export function columnsPerPage(
  containerPx: number,
  timeColPx: number,
  minCellPx: number,
  dateCount: number,
): number {
  if (dateCount < 1) return 1;
  const n = Math.floor((containerPx - timeColPx) / minCellPx);
  return Math.max(1, Math.min(dateCount, n));
}

export function deadlineInstant(wallDateTime: string, tz: string): string | null {
  const m = wallDateTime.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/);
  if (!m || !m[1] || !m[2] || !m[3]) return null;
  const date = m[1];
  const minutes = Number(m[2]) * 60 + Number(m[3]);
  const epoch = localToEpochMin(date, minutes, tz);
  if (epoch === null) return null;
  return new Date(epoch * 60_000).toISOString();
}

export function deadlineWallTime(iso: string, tz: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return "";
  const p = zonedParts(Math.floor(ms / 60_000), tz);
  const h = Math.floor(p.minutes / 60);
  const m = p.minutes % 60;
  return `${p.date}T${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function formatMinutes(minutes: number): string {
  const wrapped = ((minutes % 1440) + 1440) % 1440;
  const h = Math.floor(wrapped / 60) % 24;
  const min = wrapped % 60;
  const am = h < 12;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(min).padStart(2, "0")} ${am ? "AM" : "PM"}`;
}

export function windowLabel(start: number, end: number, tz: string): string {
  const a = zonedParts(start, tz);
  const b = zonedParts(end, tz);
  const day = formatDayHead(a.date);
  if (a.date === b.date) return `${day} ${a.label} – ${b.label} (${tz})`;
  return `${day} ${a.label} – ${formatDayHead(b.date)} ${b.label} (${tz})`;
}

export function windowLabelShort(start: number, tz: string): string {
  const a = zonedParts(start, tz);
  return `${formatDayHead(a.date)} ${a.label}`;
}

export function deadlineLabel(iso: string, viewerTz: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return "";
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: viewerTz,
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  return `Please answer by ${fmt.format(new Date(ms))} (${viewerTz})`;
}

export function pagerLabel(dates: string[], page: number, pageCount: number): string {
  const first = dates[0];
  const last = dates[dates.length - 1];
  if (!first || !last) return "";
  return `${formatDayHead(first)} to ${formatDayHead(last)} · page ${page} of ${pageCount}`;
}

export function formatDayHead(iso: string): string {
  const bits = iso.split("-");
  const y = Number(bits[0]);
  const m = Number(bits[1]);
  const d = Number(bits[2]);
  const dt = new Date(Date.UTC(y, m - 1, d, 12));
  const w = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][dt.getUTCDay()] ?? "";
  return `${w} ${m}/${d}`;
}

export function formatUpdated(iso: string, tz: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return "";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(ms));
}

export function logSentence(entry: LogEntry, tz: string): string {
  const d = entry.detail;
  if (entry.kind === "joined") return `${String(d.name ?? entry.actor)} joined`;
  if (entry.kind === "removed") {
    const name = String(d.name ?? "Someone");
    if (d.by === "organizer") return `${name} was removed by the organizer`;
    if (d.by === "self") return `${name} left the poll`;
    return `${name} removed`;
  }
  if (entry.kind === "required") {
    return d.required === true
      ? `${String(d.name ?? "Someone")} marked required`
      : `${String(d.name ?? "Someone")} unmarked required`;
  }
  if (entry.kind === "locked") {
    const start = typeof d.slotStart === "number" ? d.slotStart : Number(d.slotStart);
    if (Number.isFinite(start)) return `Locked ${windowLabelShort(start, tz)}`;
    return "Locked";
  }
  if (entry.kind === "unlocked") return "Unlocked";
  if (entry.kind === "patched") {
    const cells = typeof d.cells === "number" ? d.cells : 0;
    const people = typeof d.people === "number" ? d.people : 0;
    const fields = Array.isArray(d.fields) ? d.fields : [];
    if (fields.includes("dates")) return `Dates changed: ${cells} answers removed from ${people} people`;
    return `Event changed: ${cells} answers removed from ${people} people`;
  }
  if (entry.kind === "created") return "Created";
  return entry.kind;
}

export function addDays(iso: string, n: number): string {
  const bits = iso.split("-");
  const y = Number(bits[0]);
  const m = Number(bits[1]);
  const d = Number(bits[2]);
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  const p = (x: number) => String(x).padStart(2, "0");
  return `${dt.getUTCFullYear()}-${p(dt.getUTCMonth() + 1)}-${p(dt.getUTCDate())}`;
}

export function weekStart(iso: string): string {
  const bits = iso.split("-");
  const y = Number(bits[0]);
  const m = Number(bits[1]);
  const d = Number(bits[2]);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() - dt.getUTCDay());
  const p = (x: number) => String(x).padStart(2, "0");
  return `${dt.getUTCFullYear()}-${p(dt.getUTCMonth() + 1)}-${p(dt.getUTCDate())}`;
}
